/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type { ISchemaBoundValidationContext, IValidator } from 'validx'

/**
 * Validates an object that has a `validation` context.
 */
export function validateObject<F extends object>(
  message = 'This field is invalid.',
): IValidator<F> {
  return function validateObjectValidator({ value }) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const validation = value?.validation as ISchemaBoundValidationContext<F>
    if (validation) {
      return validation.reset().validate().isValid || message
    }

    return true
  }
}

/**
 * The value must be a valid tax form input field.
 *
 * @param message
 */
export function mustBeValidTaxFormInputValue<T>(
  message = 'Only letters, numbers and basic symbols are allowed',
): IValidator<T> {
  return function validateTaxFormInputValue(ctx) {
    const value: string = ctx.value ?? ''
    if (!value) {
      // Required-ness is handled elsewhere.
      return true
    }

    const regex = /^[a-zA-Z0-9 .\-,+&%]*$/
    if (!regex.test(value.trim())) {
      return message
    }
    return true
  }
}

/**
 * Validates that the string is a valid social security number or ITIN number.
 */
export function mustBeValidPersonalTaxpayerIdentificationNumber<T>(
  message = 'Please enter a valid SSN or ITIN',
): IValidator<T> {
  return function validateSsnValidator(ctx) {
    const value: string = ctx.value ?? ''
    const ssnRegex = /^(?!000|666|9\d{2})\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/
    const itinRegex = /^9\d{2}-\d{2}-(?!0000)\d{4}$/
    return ssnRegex.test(value) || itinRegex.test(value) || message
  }
}

/**
 * Validates that the string is a valid routing number.
 */
export function mustBeValidRoutingNumber<T>(): IValidator<T> {
  return function validateRoutingNumber(ctx) {
    // Required check should be done elsewhere.
    if (!ctx.value) {
      return true
    }

    const value: string = ctx.value ?? ''
    const regex = /^[0-9]{9}$/ // 9 digits
    return regex.test(value) || 'Routing number must be 9 digits'
  }
}

/**
 * Validates that the string is a valid account number.
 */
export function mustBeValidAccountNumber<T>(): IValidator<T> {
  return function validateAccountNumber(ctx) {
    const value: string = ctx.value ?? ''
    // Required check should be done elsewhere.
    if (!value) {
      return true
    }

    if (value.length < 4) {
      return 'Account number must be at least 4 letters and/or numbers'
    }

    if (value.length > 30) {
      return 'Account number must be less than 30 characters'
    }

    const regex = /^[a-zA-Z0-9]+$/ // Only letters and numbers
    return (
      regex.test(value) ||
      'Account number must only contain letters and numbers'
    )
  }
}

/**
 * Validates that the string is a valid EIN.
 */
export function mustBeValidEin<T>(
  message = 'EIN must be in format XX-XXXXXXX',
): IValidator<T> {
  return function validateEin(ctx) {
    const value: string = ctx.value ?? ''
    if (!value) {
      return true
    }
    const regex = /^\d{2}-\d{7}$/
    return regex.test(value) || message
  }
}

/**
 * Validates that the date is in the past.
 * @param message
 * @param relativeTo
 */
export function mustBeInThePast<T>(
  message = 'The date must be in the past',
  relativeTo?: () => Date,
): IValidator<T> {
  relativeTo ??= () => new Date()
  return function validateDateInThePast(ctx) {
    const value: Date | null | undefined = ctx.value

    // Required check should be done elsewhere.
    if (!(value instanceof Date)) {
      return true
    }

    return value.getTime() <= relativeTo().getTime() || message
  }
}

/**
 * Validates that the string does not exceed the maximum length.
 */
export function mustHaveMaxLength<T>(
  maxLength: number,
  message = `The value must not exceed ${maxLength} characters`,
): IValidator<T> {
  return function validateMaxLength(ctx) {
    const value: string = ctx.value ?? ''

    // If no value, length validation passes (required-ness handled elsewhere)
    if (!value) {
      return true
    }

    return value.length <= maxLength || message
  }
}

/**
 * Validates that the string has at least a minimum length.
 */
export function mustHaveMinLength<T>(
  minLength: number,
  message = `The value must not be less than ${minLength} characters`,
): IValidator<T> {
  return function validateMinLength(ctx) {
    const value: string = ctx.value ?? ''

    // If no value, length validation passes (required-ness handled elsewhere)
    if (!value) {
      return true
    }

    return value.length >= minLength || message
  }
}

/**
 * Validates that the string is a valid U.S. phone number with area code.
 * Optionally accepts country code (+1 or 1).
 * Examples: (555) 555-1234, 555-555-1234, +1-555-555-1234, 1 555 555 1234, etc.
 */
export function isValidUsPhoneNumber<T>(
  message = 'Please enter a valid U.S. phone number',
): IValidator<T> {
  return function validatePhoneNumber(ctx) {
    const value: string = ctx.value ?? ''

    // Required check should be done elsewhere
    if (!value) {
      return true
    }

    // Remove all non-digit characters except + at the beginning
    const digitsOnly = value.replace(/^\+?1?/, '').replace(/\D/g, '')

    // Must have exactly 10 digits (area code + 7-digit number)
    if (digitsOnly.length === 10) {
      // Basic format validation - very lenient
      // Accepts any combination of digits, spaces, dashes, parentheses, dots, and + for country code
      const phoneRegex = /^[+]?1?[\d\s\-().]+$/
      return phoneRegex.test(value) || message
    }

    return message
  }
}

/**
 * Only runs the validator if the condition is true.
 */
export function when<T>(
  condition: () => boolean,
  validator: IValidator<T>,
): IValidator<T> {
  return function validateConditional(ctx) {
    return condition() ? validator(ctx) : true
  }
}

/**
 * Validates that the value is within a numeric range.
 */
export function mustBeInRange<T>(
  min: number,
  max: number,
  message = `Value must be between ${min} and ${max}`,
): IValidator<T> {
  return function validateInRange(ctx) {
    const value: string | number = ctx.value ?? ''
    if (!value && value !== 0) return true

    const numValue = typeof value === 'string' ? Number(value) : value
    if (isNaN(numValue)) return message

    return (numValue >= min && numValue <= max) || message
  }
}
